import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { ConfirmationModal } from "../../../components/ConfirmModal";
import { CustomButton, IButtonVariant } from "../../../components/CustomButton";
import { showCustomToast } from "../../../components/CustomToast";
import { useModal } from "../../../hooks/useModal";
import { putUpdateBillingThunk, submitBillingThunk, updateBillingCurrentStep } from "../../../reducers/billingReducer";
import { getBillingIsUpdating, getIsBillingInNewStatus } from "../../../selectors/billing";
import { useAppDispatch } from "../../../store";
import { BillingStep } from "../../../types/billing";
import { BillingLocationsTableType } from "../../../types/billingLocations";
import { BillingCalculationStats } from "../BillingCalculationStats";
import { BillingFeeCalculation } from "../BillingFeeCalculation";
import { BillingLocationsTableContainer } from "../BillingLocationsTable/BillingLocationsTableContainer";
import { BillingPaymentSchedule } from "../BillingPaymentSchedule";

import styles from "./BillingPageCalculation.module.scss";

export const BillingPageCalculation: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { isModalVisible, toggleModalVisibility } = useModal();

  const isUpdating = useSelector(getBillingIsUpdating);
  const isBillingInNewStatus = useSelector(getIsBillingInNewStatus);

  const handleSave = async () => {
    const result = await dispatch(putUpdateBillingThunk());
    if (putUpdateBillingThunk.fulfilled.match(result)) {
      showCustomToast(t("billingPageCalculation.saveSuccess"), { type: "success" });
    } else if (putUpdateBillingThunk.rejected.match(result)) {
      showCustomToast(t(`billingApiErrors.${result.payload}`), { type: "error" });
    }
  };

  const handleSaveAndSend = async () => {
    const putResult = await dispatch(putUpdateBillingThunk());
    if (putUpdateBillingThunk.rejected.match(putResult)) {
      showCustomToast(t(`billingApiErrors.${putResult.payload}`), { type: "error" });
      return;
    }

    const submitResult = await dispatch(submitBillingThunk());
    if (submitBillingThunk.rejected.match(submitResult)) {
      showCustomToast(t(`billingApiErrors.${submitResult.payload}`), { type: "error" });
      return;
    }

    showCustomToast(t("billingPageCalculation.saveAndSendSuccess"), { type: "success" });
    dispatch(updateBillingCurrentStep(BillingStep.Billing));
  };

  const handleContinue = () => {
    dispatch(updateBillingCurrentStep(BillingStep.Billing));
  };

  return (
    <>
      <div className={styles.calculationContainer}>
        <BillingFeeCalculation />
        <BillingCalculationStats />
      </div>
      <BillingPaymentSchedule />
      <BillingLocationsTableContainer tableType={BillingLocationsTableType.Calculation} />
      <div className={styles.buttonContainer}>
        {isBillingInNewStatus ? (
          <>
            <CustomButton
              onClick={toggleModalVisibility}
              text={t("billingPageCalculation.saveAndSend")}
              disabled={isUpdating}
              variant={IButtonVariant.white}
            />
            <CustomButton onClick={handleSave} text={t("billingPageCalculation.save")} disabled={isUpdating} />
          </>
        ) : (
          <CustomButton onClick={handleContinue} text={t("billingPageCalculation.continue")} />
        )}
      </div>
      <ConfirmationModal
        title={t("billingPageCalculation.confirmTitle")}
        text={t("billingPageCalculation.confirmText")}
        isModalVisible={isModalVisible}
        toggleModalVisibility={toggleModalVisibility}
        onSuccess={handleSaveAndSend}
      />
    </>
  );
};
